export const AUTH_SESSION_STORAGE_KEY = 'evault.authSession';

function storage() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  return window.sessionStorage;
}

/** Access token kept in memory (not persisted). */
let accessTokenMemory = null;

export function getAccessToken() {
  return accessTokenMemory;
}

export function setAccessToken(token) {
  accessTokenMemory = token || null;
}

export function clearAccessToken() {
  accessTokenMemory = null;
}

export function readAuthSessionMeta() {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.orgId || !parsed.orgType) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeAuthSessionMeta(meta) {
  const store = storage();
  if (!store || !meta) return null;
  store.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(meta));
  return meta;
}

export function clearAuthSessionMeta() {
  const store = storage();
  if (store) {
    store.removeItem(AUTH_SESSION_STORAGE_KEY);
  }
  clearAccessToken();
}

/**
 * Store a `/v1/auth/session` or `/v1/auth/refresh` token pair: access token
 * in memory, everything else (incl. the refresh token) in sessionStorage.
 * Returns the session meta that was written.
 */
export function persistTokenPair(tokenPair, { authProvider } = {}) {
  setAccessToken(tokenPair.access_token);
  const meta = {
    userId: tokenPair.user_id,
    email: tokenPair.email,
    fullName: tokenPair.full_name,
    orgId: tokenPair.org_id,
    orgType: tokenPair.org_type,
    organizationName: tokenPair.organization_name,
    membershipRole: tokenPair.membership_role,
    permissions: tokenPair.permissions || [],
    isPlatformAdmin: Boolean(tokenPair.is_platform_admin),
    refreshToken: tokenPair.refresh_token,
    accessTokenPresent: true,
    authProvider: authProvider || 'password',
  };
  writeAuthSessionMeta(meta);
  return meta;
}

/** `exp` (ms since epoch) of a JWT, or null when it cannot be read. */
export function accessTokenExpiresAt(token = accessTokenMemory) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Map JWT session meta to the shape consumers expect from demo session.
 */
export function toWorkspaceSession(meta) {
  if (!meta) return null;
  const orgType = String(meta.orgType || '').toLowerCase();
  const initials = (meta.fullName || meta.email || 'U')
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return {
    workspaceId: orgType,
    tenantId: null,
    role: orgType,
    organizationName: meta.organizationName || '',
    roleLabel: orgType.charAt(0).toUpperCase() + orgType.slice(1),
    userDisplayName: meta.fullName || meta.email || 'User',
    userEmail: meta.email || '',
    initials: initials || 'U',
    userId: meta.userId || null,
    orgId: meta.orgId || null,
    configured: Boolean(meta.accessTokenPresent || getAccessToken()),
    membershipRole: meta.membershipRole || null,
    permissions: meta.permissions || [],
    isPlatformAdmin: Boolean(meta.isPlatformAdmin),
    refreshToken: meta.refreshToken || null,
  };
}
